// The monthly invoice Eighty Days sends a partner.
//
// One document per partner per month, raised from the bookings that partner had
// delivered in that month. It is produced here and *sent* from /portal, which is
// where the commercial half lives — when it went out, when it is due, how it was
// paid, whether it has been. This side owns what can be derived from the
// bookings; that side owns what happens to the document afterwards.
//
// Both halves of the arithmetic live in this file rather than in the Cloud
// Function, for the same reason `validateBookingRequest` does: the admin screen
// previews a draft before anybody raises it, and a preview that computed the
// total differently from the invoice would be worse than no preview at all.
//
// Money is JPY throughout, and every amount is **tax-inclusive** (税込). The
// invoice states a rate per line and computes no tax at all — see
// `InvoiceLineType.taxRate`.

import {
  bookingPriceLines,
  jstDateOf,
  type BookingCommissionType,
  type BookingPriceLineType,
  type BookingRequestType,
  type PortalSyncStateType,
} from './types_bookingRequest';

// Firestore collection name.
export const coll_invoices = 'invoices';

// --- Status ---

/**
 * Where an invoice has got to.
 *
 * 'draft' is recomputed from the bookings every time it is generated and binds
 * nobody. 'issued' is the moment it stops being ours to change: the bookings are
 * stamped with its id, the number is allocated, and the PDF is rendered. There is
 * no going back from it — a mistake is voided and corrected by an adjustment on
 * the next invoice, because the partner already has the first one.
 */
export type InvoiceStatusType = 'draft' | 'issued' | 'void';

export const invoiceStatusLabels: Record<InvoiceStatusType, string> = {
  draft: 'Draft',
  issued: 'Issued',
  void: 'Void',
};

// --- Lines ---

/**
 * What a line is charging for.
 *
 * 'cancellation-fee' and 'credit' are both consequences of a cancellation and
 * are still kept apart: a fee is a new charge the partner owes, a credit is the
 * reversal of a charge already sent. A cancellation after its month has been
 * invoiced produces *both*, and rolling them into one net figure would leave the
 * partner unable to see which of the two it is querying.
 *
 * 'service' is the rest of what a booking cost — the transfer, the interpreter,
 * the lunch. Its own kind rather than a second 'experience' line because it is
 * the kind that usually carries no commission, and the deduction block is stated
 * as a percentage: a partner checking `Commission deduction (10%)` against the
 * subtotal has to be able to see which lines it was 10% *of*. See
 * `BookingPriceLineType`.
 */
export type InvoiceLineKindType = 'experience' | 'service' | 'cancellation-fee' | 'credit';

export interface InvoiceLineType {
  kind: InvoiceLineKindType;

  /** The booking this came from. Never cleared — it is how a query is answered. */
  requestId: string;

  /**
   * The booking's request code, where it had one when the draft was built — the
   * 参照番号 column on the invoice, and the string a partner will quote when they
   * query a line.
   *
   * Copied onto the line rather than read back off the booking, like every other
   * fact here: an invoice states what was charged and what it was called at the
   * time, and re-cutting a booking's code afterwards must not silently reword a
   * document the partner is already holding.
   *
   * Absent on a booking that had no code, and on every line drafted before codes
   * existed. `invoiceLineReference` reads it back with the fallback.
   */
  requestCode?: string;

  /**
   * `yyyy-mm-dd`. The date that put this line in this period, which is not the
   * same question for every kind — see `buildInvoiceLines`.
   */
  date: string;

  experienceName: string;
  guestName: string;
  guests: number;

  /**
   * What this line is, where it is not the experience itself: the label off the
   * booking's price row. Set on a 'service' line and on the credit reversing one,
   * absent everywhere else.
   *
   * Copied rather than looked up, like everything else on a line — an invoice
   * states what was charged, and editing a booking afterwards must not reword a
   * document the partner is holding.
   */
  serviceLabel?: string;

  /** JPY, tax-inclusive. Negative on a credit. */
  gross: number;

  /**
   * The rate this line is charged at, as a whole percent. Stated on the invoice
   * (税率) and never used to compute anything: `gross` already includes tax, so
   * there is no tax for this to work out. It exists so a 軽減税率 line can be
   * marked ※ against the footnote the template carries.
   */
  taxRate: number;

  /**
   * The partner's commission on this line, at the rate frozen onto the booking.
   * Zero where the experience has commission switched off.
   */
  commissionPercent: number;
  commissionAmount: number;
}

/** The default 税率. Nothing sets the 8% 軽減税率 yet; see `InvoiceLineType`. */
export const invoiceStandardTaxRate = 10;
export const invoiceReducedTaxRate = 8;

// --- The invoice ---

/** The partner's accounting details as they stood when the invoice was raised. */
export interface InvoiceBillToType {
  /**
   * The accounts *contact* — a person. Held so we know who the document went to,
   * and deliberately not what it is addressed to: 御中 is for an organisation and
   * takes the partner's own name. See `drawHeader`.
   */
  accountingName: string;
  accountingEmail: string;
  billingAddress: string;
}

/** One `Commission deduction` row in the totals block. */
export interface InvoiceCommissionDeductionType {
  percent: number;
  /** JPY, positive. Rendered negative. */
  amount: number;
}

export interface InvoiceType {
  /** `{partnerId}_{yyyy-mm}` — derived, not generated. See `invoiceDocId`. */
  id: string;

  partnerId: string;
  /** Denormalised so the list reads without a lookup per row, as elsewhere. */
  partnerName: string;

  /** `yyyy-mm`, JST. The month being billed. */
  period: string;

  status: InvoiceStatusType;

  /** 件名. Defaults from the period; an admin may reword it while it is a draft. */
  subject: string;

  lines: InvoiceLineType[];

  /**
   * One row per distinct commission rate in play, because `commissionPercent`
   * belongs to the experience: a month mixing a 10% experience with a 15% one
   * cannot be stated as a single percentage of the total. Usually one row.
   */
  commissionDeductions: InvoiceCommissionDeductionType[];
  /** Sum of the lines. Tax-inclusive. */
  subtotalGross: number;
  totalCommission: number;
  /** 合計（税込）. What the partner owes. */
  total: number;

  createdAt: string;
  updatedAt?: string;

  // --- Set at issue, and not before ---

  /** 請求書番号. See `invoiceNumberFor`. */
  invoiceNumber?: string;
  /**
   * The partner's accounting block, copied at issue rather than read live. An
   * invoice states where it was sent; a partner that moves office afterwards has
   * not changed where last month's invoice went.
   */
  billTo?: InvoiceBillToType;
  issuedAt?: string;
  issuedByUid?: string;
  /** 担当 — who at Eighty Days raised it. */
  issuedByName?: string;
  /** `yyyy-mm-dd`. 支払期限. See `invoiceDueDate`. */
  dueDate?: string;

  /**
   * Where the rendered PDF landed in Storage.
   *
   * A path rather than a URL. Reading it back goes through the Storage SDK from
   * an admin's browser, which the storage rules already gate — so there is no
   * signed URL to mint, nothing with an expiry to keep fresh, and no link that
   * would still open the document after somebody's access was removed.
   */
  pdfPath?: string;
  pdfGeneratedAt?: string;
  /** Set when the render failed. The invoice is still issued — see `issueInvoice`. */
  pdfError?: string;

  // --- /portal ---
  //
  // Tracked apart from `status` for the same reason a booking's sync is: what we
  // have raised is not conditional on /portal having heard about it. Nothing
  // writes anything but 'pending' yet.

  portalSyncState?: PortalSyncStateType;
  portalInvoiceId?: string;
  portalError?: string;

  voidedAt?: string;
  voidedByUid?: string;
}

// --- Identity ---

/**
 * The document id, derived from what the invoice *is* rather than generated.
 *
 * Which is what makes generating a draft idempotent: pressing the button twice,
 * or two admins pressing it at once, updates one document instead of leaving two
 * invoices for the same partner and month for somebody to notice later.
 */
export function invoiceDocId(partnerId: string, period: string): string {
  return `${partnerId}_${period}`;
}

/**
 * 請求書番号, allocated at issue.
 *
 * Derived rather than counted, so it needs no counter document, no allocation
 * inside the transaction, and stays reproducible: an invoice rebuilt from its
 * bookings carries the number it always had. The trade is that the series has
 * gaps — a partner with no bookings in a month has no number — which a derived
 * number cannot avoid and which nothing here depends on.
 */
export function invoiceNumberFor(partnerId: string, period: string): string {
  return `80D-${period.replace('-', '')}-${partnerId.toUpperCase()}`;
}

/** `yyyy-mm` of a `yyyy-mm-dd`. */
export function invoicePeriodOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

/** A period `months` either side of another. Negative goes back. */
export function shiftPeriod(period: string, months: number): string {
  const [year, month] = period.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return shifted.toISOString().slice(0, 7);
}

/**
 * The most recent month that has finished, which is what the invoice screen
 * opens on.
 *
 * The month in progress is the wrong default: it is incomplete by definition,
 * and a draft raised from it would leave out every booking still to be delivered
 * before the month is out. An admin who wants it can still step forward to it.
 *
 * Read in JST, because that is the calendar the bookings are dated in — on the
 * first of the month a browser in London would otherwise still be offering the
 * month before last.
 */
export function lastCompletePeriod(now: Date = new Date()): string {
  const jstToday = new Date(now.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return shiftPeriod(invoicePeriodOf(jstToday), -1);
}

/**
 * 支払期限: the last day of the month after the one being billed.
 *
 * Day 0 of the month after next is the last day of the month after this one, so
 * this needs no table of month lengths and gets February right on its own.
 */
export function invoiceDueDate(period: string): string {
  const [year, month] = period.split('-').map(Number);
  return new Date(Date.UTC(year, month + 1, 0)).toISOString().slice(0, 10);
}

/** 件名, as it reads before anybody rewords it. */
export function makeInvoiceSubject(period: string): string {
  const [year, month] = period.split('-').map(Number);
  return `${year}年${month}月分 体験ご利用料金`;
}

const englishMonths = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** How a period reads to a person: `June 2026`. */
export function invoicePeriodLabel(period: string): string {
  const [year, month] = period.split('-').map(Number);
  return `${englishMonths[month - 1]} ${year}`;
}

/** The filename the PDF is stored and downloaded under, matching the template's. */
export function invoicePdfFilename(partnerName: string, period: string): string {
  return `請求書：${partnerName}_${invoicePeriodLabel(period)}.pdf`;
}

// --- Arithmetic ---

/**
 * The partner's commission on an amount, in whole yen.
 *
 * Floored, and floored on the *magnitude* so the sign survives: a credit
 * reversing an experience line has to give back exactly what was deducted, and
 * `Math.floor(-x)` is not `-Math.floor(x)`. Getting that wrong is a yen a month
 * that nobody can account for.
 */
export function commissionFor(gross: number, percent: number): number {
  if (!percent) return 0;
  return Math.sign(gross) * Math.floor((Math.abs(gross) * percent) / 100);
}

/**
 * The consumption tax *inside* a tax-inclusive amount, in whole yen.
 *
 * Every figure on this invoice is 税込, so the tax is not added to anything —
 * it is extracted: `gross × rate / (100 + rate)`, not `gross × rate / 100`.
 *
 * Floored on the magnitude, as `commissionFor` is, and for the same reason: a
 * credit carries negative gross and has to give back exactly the tax its charge
 * carried.
 */
export function taxWithin(gross: number, rate: number): number {
  if (!rate) return 0;
  return Math.sign(gross) * Math.floor((Math.abs(gross) * rate) / (100 + rate));
}

/** The 消費税額 the invoice contains at one rate, and what it was worked out from. */
export interface InvoiceTaxByRateType {
  /** The 税率, as a whole percent. */
  rate: number;
  /** The 税込 consideration charged at it, commission already off — the 対価の額. */
  taxable: number;
  /** The tax inside that. */
  tax: number;
}

/**
 * The consumption tax the invoice contains, one row per 税率.
 *
 * The base is **合計** — what the partner actually owes — not 小計. The
 * commission is treated as reducing the consideration for what we supplied
 * rather than as a separate charge the partner makes back to us, so it comes off
 * before the tax is extracted and these rows sum to the tax inside the grand
 * total. A reader can check the figure against the 合計 immediately above it,
 * which is the whole reason it is printed there.
 *
 * Netted **per line** rather than by taking the commission off the rate's
 * subtotal, because the two groupings are not the same one: `commissionPercent`
 * belongs to the experience and `taxRate` to what was sold, so a month mixing
 * rates cannot have its deductions apportioned after the fact. Every line knows
 * its own commission, so netting there needs no apportionment and the rows still
 * add up to `invoiceTotals().total` exactly.
 *
 * Rounded **once per rate**, not once per line: 消費税 is worked out on the
 * rate's total, and flooring each line first would lose up to a yen per line to
 * rounding — a difference that grows with how many bookings a partner had, which
 * is not a property a tax figure is allowed to have.
 *
 * Grouped and stated per rate rather than as one number because that is what a
 * 適格請求書 has to show: 税率ごとに区分した消費税額等. Today every line is 10%
 * and there is one row; a 軽減税率 line would produce a second on its own.
 */
export function invoiceTaxByRate(lines: InvoiceLineType[]): InvoiceTaxByRateType[] {
  const byRate = new Map<number, number>();
  for (const line of lines) {
    const net = line.gross - line.commissionAmount;
    byRate.set(line.taxRate, (byRate.get(line.taxRate) ?? 0) + net);
  }

  return [...byRate.entries()]
    .map(([rate, taxable]) => ({ rate, taxable, tax: taxWithin(taxable, rate) }))
    .sort((a, b) => b.rate - a.rate);
}

/**
 * A commission as it reads in a deduction column, given whatever formatter the
 * surface states money with.
 *
 * Shared rather than written at each site for the reason `invoiceLineDescription`
 * is: the admin's table and the document the partner receives have to state a
 * deduction the same way, and the sign rule is the part that is easy to get
 * wrong twice.
 *
 * That rule is the whole of it. An ordinary commission is taken off, so it is
 * written negative. A credit's commission is *already* negative — it gives back
 * what its charge deducted — and prefixing that with a minus of our own prints
 * `--1,200`, which reads as neither a charge nor a refund. It is money coming
 * back, so it is written `+`.
 */
export function formatCommissionDeduction(amount: number, formatAmount: (n: number) => string): string {
  if (!amount) return '—';
  return `${amount > 0 ? '-' : '+'}${formatAmount(Math.abs(amount))}`;
}

/**
 * The ＊ marking a line the commission was *not* taken from.
 *
 * A partner checking `Commission deduction (10%)` against the lines has otherwise
 * no way to tell which of them it was 10% *of*. Marking the exceptions rather
 * than the rule, because on a typical month nearly every line is commissioned:
 * marking those put a ＊ against almost the whole 摘要 column to single out the
 * one service line that carried none.
 *
 * Read off `commissionPercent` and not `commissionAmount`, so this states what
 * the line *is* rather than what it happened to come to: a ¥0 line at 10%, or
 * one small enough that the floored commission lands on zero, is still charged
 * at 10% and saying otherwise would be untrue.
 *
 * Nothing is marked on an invoice that deducts no commission at all — a ＊
 * against every line, answering to a totals block with no deduction in it, is
 * noise. Which is why this takes the deductions.
 *
 * Fullwidth ＊, to sit at the size of the ※ it shares the 摘要 column with.
 */
export function invoiceLineCommissionMark(
  line: InvoiceLineType,
  deductions: InvoiceCommissionDeductionType[],
): string {
  if (!deductions.length) return '';
  return line.commissionPercent ? '' : '＊';
}

/**
 * The rate a commissioned line was charged at, where the invoice deducts at more
 * than one — `（手数料10%）` against the `Commission deduction (10%)` row.
 *
 * Only then: with a single deduction row there is nothing to disambiguate, and
 * repeating the one rate down every line is the clutter the ＊ was inverted to
 * avoid.
 *
 * Stated only for a rate the totals block actually prints a row for. A charge
 * and the credit reversing it in the same month cancel to nothing and
 * `invoiceTotals` drops the row; annotating those lines would leave the reader
 * hunting for a deduction the invoice does not make.
 */
export function invoiceLineCommissionRate(
  line: InvoiceLineType,
  deductions: InvoiceCommissionDeductionType[],
): string {
  if (deductions.length < 2 || !line.commissionPercent) return '';
  if (!deductions.some((deduction) => deduction.percent === line.commissionPercent)) return '';
  return `（手数料${line.commissionPercent}%）`;
}

/** The totals block, from the lines. */
export function invoiceTotals(lines: InvoiceLineType[]): {
  commissionDeductions: InvoiceCommissionDeductionType[];
  subtotalGross: number;
  totalCommission: number;
  total: number;
} {
  const subtotalGross = lines.reduce((sum, line) => sum + line.gross, 0);

  const byRate = new Map<number, number>();
  for (const line of lines) {
    if (!line.commissionAmount) continue;
    byRate.set(line.commissionPercent, (byRate.get(line.commissionPercent) ?? 0) + line.commissionAmount);
  }

  const commissionDeductions = [...byRate.entries()]
    .map(([percent, amount]) => ({ percent, amount }))
    .filter((d) => d.amount !== 0)
    .sort((a, b) => b.percent - a.percent);

  const totalCommission = commissionDeductions.reduce((sum, d) => sum + d.amount, 0);

  return { commissionDeductions, subtotalGross, totalCommission, total: subtotalGross - totalCommission };
}

/**
 * 摘要 — what one line says it is for.
 *
 * Written here rather than in the PDF renderer so the admin's draft table and
 * the document the partner receives describe a line with the same words. Reword in
 * one place and both follow.
 */
export function invoiceLineDescription(line: InvoiceLineType): string {
  const [, month, day] = line.date.split('-').map(Number);
  const when = `${month}月${day}日`;

  switch (line.kind) {
    case 'experience':
      return `${line.experienceName}（${when}）／${line.guestName} ${line.guests}名`;
    // Indented and short, because it is read against the experience line
    // immediately above it — the lines are sorted to keep them together. The date
    // is repeated all the same, so a service that wraps onto a continuation sheet
    // can still be checked on its own.
    // The ideographic space is written as an escape because a literal one is
    // indistinguishable from an ordinary indent in this file — and it is the
    // indent the JP font actually renders.
    case 'service':
      return `\u3000\u2514 ${line.serviceLabel ?? ''}（${when}）`;
    case 'cancellation-fee':
      return `キャンセル料：${line.experienceName}（${when}）／${line.guestName}`;
    case 'credit':
      return `取消返金：${line.serviceLabel ?? line.experienceName}（${when}）／${line.guestName}`;
  }
}

/**
 * The same line, for a table that already states the date and the guest in
 * columns of their own — which is what the PDF's 摘要 column is.
 *
 * Everything `invoiceLineDescription` says apart from those two, so the two
 * surfaces still name a line with the same words and the document does not print
 * the same date twice on one row. The guest *count* stays here rather than
 * moving to the client column: it qualifies what was delivered, and a service or
 * a credit does not carry one of its own.
 */
export function invoiceLineName(line: InvoiceLineType): string {
  switch (line.kind) {
    case 'experience':
      return `${line.experienceName}（${line.guests}名）`;
    // Indented, as in `invoiceLineDescription` and for the same reason: it is read
    // against the experience line immediately above it. The date it repeated there
    // is now in the row's own date column, so a service that wraps onto a
    // continuation sheet can still be checked on its own. The ideographic space is
    // an escape, as it is above: a literal one reads as an ordinary indent here.
    case 'service':
      return `\u3000\u2514 ${line.serviceLabel ?? ''}`;
    case 'cancellation-fee':
      return `キャンセル料：${line.experienceName}`;
    case 'credit':
      return `取消返金：${line.serviceLabel ?? line.experienceName}`;
  }
}

// --- Selecting the bookings ---

/**
 * The lines a partner's bookings produce for one month.
 *
 * Three kinds, and which date decides the month is a different question for
 * each — the asymmetry is deliberate:
 *
 *   - an **experience** is billed in the month it was *delivered*, so it is
 *     dated by `confirmedDate`. That is what a partner expects to be invoiced for;
 *     a booking taken in April for September is September's business.
 *   - a **cancellation fee** is dated by `cancelledAt`, because it is billed in
 *     the month it was *incurred*. Dating it by the booked date would be worse
 *     than wrong: cancelling a September booking in August means September never
 *     happens, so the fee would sit waiting for a month that produces no line
 *     for this booking at all.
 *   - a **credit** is the same event seen from the other side — a booking whose
 *     experience charge has already gone out on an invoice the partner is holding.
 *     It reverses that charge, commission and all, and is dated with the fee it
 *     travels beside.
 *
 * `invoiceId` and `adjustmentInvoiceId` are what stop any of the three being
 * raised twice. They are stamped when an invoice is issued, never when it is
 * drafted, so a draft can be regenerated as often as anybody likes.
 *
 * A month bills everything unbilled **up to the end of it**, not only what falls
 * inside it. The difference is not pedantry — without it money is quietly lost.
 * Issue June's invoice on the 20th, have a partner cancel on the 25th, and the fee
 * that cancellation owes is dated June: it missed the invoice that has gone out,
 * and a strict month would never look at June again. Sweeping forward means a
 * straggler lands on the next invoice raised instead of nowhere, and it cannot
 * double-bill, because anything already billed carries a stamp. Each line still
 * shows its own date, so a June line on a July invoice reads as what it is.
 *
 * `commissionOf` is passed in rather than read off the experience here, because
 * this has to stay a pure function of the bookings: the Cloud Function resolves
 * it from the experiences it loaded in its transaction, and the admin preview
 * from its own subscription. See `resolveBookingCommission`.
 */
/**
 * What the 参照番号 column prints: the booking's request code, or its document id
 * where it never had one.
 *
 * The same fallback `bookingReference` makes, and for the same reason — a line
 * a partner cannot quote back at us is a line they have to describe instead.
 */
export function invoiceLineReference(line: Pick<InvoiceLineType, 'requestId' | 'requestCode'>): string {
  return line.requestCode || line.requestId;
}

export function buildInvoiceLines(
  requests: BookingRequestType[],
  period: string,
  commissionOf: (request: BookingRequestType) => BookingCommissionType,
  taxRate: number = invoiceStandardTaxRate,
): InvoiceLineType[] {
  const lines: InvoiceLineType[] = [];

  for (const request of requests) {
    const commission = commissionOf(request);
    const percent = commission.enabled ? commission.percent : 0;
    const guestName = `${request.customerFirstName} ${request.customerLastName}`.trim();

    const base = {
      requestId: request.id,
      requestCode: request.portalRequestCode,
      experienceName: request.experienceName,
      guestName,
      guests: request.guests,
      taxRate,
    };

    // How the booking was priced, one invoice line per row. A booking with no
    // price at all still bills a ¥0 experience line, as it always has: the
    // experience was delivered, and a line an admin can query beats a booking
    // that silently produces nothing.
    const priced = bookingPriceLines(request);
    const priceRows = priced.length
      ? priced
      : [{ label: request.experienceName, amount: 0, commissionable: true }];

    /**
     * One line from one price row.
     *
     * The rate is the row's business, not the booking's — a non-commissionable
     * row is charged at 0% however generous the partner's arrangement is, which is
     * the whole reason the rows are itemised. `commissionFor` takes the sign from
     * `gross`, so a credit gives back exactly what its charge deducted.
     */
    const pricedLine = (
      row: BookingPriceLineType,
      i: number,
      { kind, date, sign }: { kind: InvoiceLineKindType; date: string; sign: 1 | -1 },
    ): InvoiceLineType => {
      const rowPercent = row.commissionable ? percent : 0;
      // Zero has no sign on an invoice. `-1 * 0` is `-0` in Javascript, and it
      // survives all the way to `Intl.NumberFormat`, which prints it as `-¥0`.
      const gross = row.amount === 0 ? 0 : sign * row.amount;
      return {
        ...base,
        kind,
        // The first row is the experience the partner asked for; the rest are what
        // was arranged around it. Left off the experience line rather than set to
        // its own name, so nothing has to know which of the two it is holding.
        ...(i === 0 ? {} : { serviceLabel: row.label }),
        date,
        gross,
        commissionPercent: rowPercent,
        commissionAmount: commissionFor(gross, rowPercent),
      };
    };

    // The experience itself, in the month it ran.
    if (
      request.status === 'confirmed'
      && !request.invoiceId
      && request.confirmedDate
      && invoicePeriodOf(request.confirmedDate) <= period
    ) {
      const date = request.confirmedDate;
      lines.push(
        ...priceRows.map((row, i) => pricedLine(row, i, { kind: i === 0 ? 'experience' : 'service', date, sign: 1 })),
      );
    }

    // Everything a cancellation owes, in the month it was cancelled.
    if (request.status === 'cancelled' && !request.adjustmentInvoiceId && request.cancelledAt) {
      const cancelledOn = jstDateOf(request.cancelledAt);
      if (invoicePeriodOf(cancelledOn) <= period) {
        // Only for a booking already billed. One cancelled before its invoice
        // went out simply never produces an experience line, which needs no
        // reversal — `invoiceId` unset is exactly that case.
        //
        // Reversed row by row rather than as one net figure, so each credit gives
        // back exactly what its own charge took — including the commission, which
        // differs per row. A partner querying one line of last month's invoice can
        // then find the line that reversed it.
        if (request.invoiceId) {
          lines.push(
            ...priceRows.map((row, i) => pricedLine(row, i, { kind: 'credit', date: cancelledOn, sign: -1 })),
          );
        }

        // Commission is taken on a fee as it is on anything else. The partner earns
        // its share for placing the guest, and it placed one — the booking was
        // made, held a day, and the fee is what that day is being charged for. A
        // partner that lost its share whenever a guest cancelled would be paid less
        // for the same work by the one thing it does not control.
        //
        // At the booking's own rate, which is already 0 where the experience has
        // commission switched off.
        if (request.cancellationFee) {
          lines.push({
            ...base,
            kind: 'cancellation-fee',
            date: cancelledOn,
            gross: request.cancellationFee,
            commissionPercent: percent,
            commissionAmount: commissionFor(request.cancellationFee, percent),
          });
        }
      }
    }
  }

  // By date, then by kind, so a credit and the fee beside it read in that order
  // rather than in whatever order the bookings happened to arrive in.
  //
  // A service ranks with the experience it belongs to and the booking breaks the
  // tie, which is what keeps an indented service line under its own experience
  // line on a day when two partners' bookings both ran. Within one booking the sort is
  // a no-op and the order they were pushed in survives, so the experience comes
  // first and its services follow in the order the desk typed them.
  const kindOrder: Record<InvoiceLineKindType, number> = {
    experience: 0,
    service: 0,
    credit: 1,
    'cancellation-fee': 2,
  };
  return lines.sort((a, b) =>
    a.date.localeCompare(b.date)
    || kindOrder[a.kind] - kindOrder[b.kind]
    || a.requestId.localeCompare(b.requestId)
  );
}

/**
 * Whether a booking could contribute to *some* invoice, whatever the month.
 *
 * Used to narrow what has to be read before `buildInvoiceLines` is called, and
 * kept beside it so the two cannot come to disagree about what is billable.
 */
export function isBillableBooking(request: BookingRequestType): boolean {
  if (request.status === 'confirmed') return !request.invoiceId;
  if (request.status === 'cancelled') {
    return !request.adjustmentInvoiceId && (!!request.invoiceId || !!request.cancellationFee);
  }
  return false;
}

// --- Building one ---

/** A fresh draft. Totals are derived, so they are computed rather than passed. */
export function makeInvoiceDraft(
  { partnerId, partnerName, period, lines, subject, now }: {
    partnerId: string;
    partnerName: string;
    period: string;
    lines: InvoiceLineType[];
    subject?: string;
    now: string;
  },
): InvoiceType {
  return {
    id: invoiceDocId(partnerId, period),
    partnerId,
    partnerName,
    period,
    status: 'draft',
    subject: subject?.trim() || makeInvoiceSubject(period),
    lines,
    ...invoiceTotals(lines),
    createdAt: now,
    updatedAt: now,
  };
}

/** A stored invoice, with anything added to the type since filled in. */
export function normaliseInvoice(data: Partial<InvoiceType>, id: string): InvoiceType {
  return {
    partnerId: '',
    partnerName: '',
    period: '',
    status: 'draft',
    subject: '',
    lines: [],
    commissionDeductions: [],
    subtotalGross: 0,
    totalCommission: 0,
    total: 0,
    createdAt: '',
    ...data,
    id,
  };
}

// --- Callables ---

export interface GenerateInvoiceDraftRequestType {
  partnerId: string;
  /** `yyyy-mm`. */
  period: string;
  /** 件名. Omitted leaves whatever the draft already had, or the default. */
  subject?: string;
}

export interface GenerateInvoiceDraftResponseType {
  invoiceId: string;
  /** How many lines it came to. Zero is a real answer: nothing to bill. */
  lineCount: number;
  total: number;
}

export interface IssueInvoiceRequestType {
  invoiceId: string;
}

export interface IssueInvoiceResponseType {
  invoiceNumber: string;
  /** False when the PDF could not be rendered. The invoice is issued regardless. */
  pdfRendered: boolean;
}

export interface VoidInvoiceRequestType {
  invoiceId: string;
}

export interface RegenerateInvoicePdfRequestType {
  invoiceId: string;
}

export interface RegenerateInvoicePdfResponseType {
  /** When the document that now exists was produced. */
  pdfGeneratedAt: string;
}
